import { useEffect, useMemo, useRef, useState } from 'react';
import { Minus, Plus, RotateCcw, Sparkles, Check } from 'lucide-react';
import { elementColor } from '../elements';
import {
  EXAMPLE_EQUATIONS,
  balanceEquation,
  isBalanced,
  parseEquation,
  subscriptFormula,
  tally,
  type ParsedEquation,
} from './equation';
import {
  DARK_INK,
  NowYouTry,
  VizCard,
  VIZ_BTN_GHOST,
  VIZ_BTN_PRIMARY,
  VIZ_FOCUS,
  cueHighlights,
  cueIsActive,
  usePrefersReducedMotion,
  useMountSequence,
  type VizCue,
} from '../vizShared';

// EquationBalancer (type 'equation_balancer') — adjust per-species coefficients
// with ± steppers and watch a live atom-balance ledger turn green as each element
// matches. "Solve" fills the exact integer coefficients (Gaussian elimination)
// and animates particles assembling. config: { equation?, title?, examples? }.
// Resilient: bad/empty equation falls back to a built-in example.

export interface EquationBalancerConfig {
  title?: string;
  equation?: string;
  /** Show the example picker (default true). */
  examples?: boolean;
}

interface Props {
  config?: EquationBalancerConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
  // WIP-016 (design §8.3) — reports the current coefficients as a step's
  // answer, balanced or not. Optional and additive, same as LewisStructure's:
  // nothing here calls it unless a caller opts in, so every other caller of
  // EquationBalancer is unaffected.
  onStateChange?: (snapshot: { state: unknown; display: string }) => void;
}

const FALLBACK = 'H2 + O2 -> H2O';

// The equation as the student currently has it, coefficients and all — e.g.
// "2H₂ + O₂ → 2H₂O" — for the instructor's responses table (design §8.3's
// own example). Renders whatever is on screen, balanced or not; grading a
// wrong attempt is the instructor's job, same as it is for free text today.
function formatEquationDisplay(eq: ParsedEquation, coeffs: number[], nR: number): string {
  const side = (species: ParsedEquation['reactants'], offset: number) =>
    species
      .map((sp, i) => {
        const c = coeffs[offset + i] ?? 1;
        return `${c > 1 ? c : ''}${subscriptFormula(sp.formula)}`;
      })
      .join(' + ');
  return `${side(eq.reactants, 0)} → ${side(eq.products, nR)}`;
}

export default function EquationBalancer({ config = {}, onComplete, cue, onStateChange }: Props) {
  const showExamples = config.examples !== false;
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);
  const [equation, setEquation] = useState(config.equation || FALLBACK);
  const interacted = useRef(false);

  const eq: ParsedEquation = useMemo(() => {
    return parseEquation(equation) ?? parseEquation(FALLBACK)!;
  }, [equation]);

  const nR = eq.reactants.length;
  const nP = eq.products.length;
  const total = nR + nP;

  // The exact integer solution for the current equation (may be null).
  const solution = useMemo(() => balanceEquation(eq), [eq]);

  // Coefficient state — one entry per species (reactants then products).
  const [coeffs, setCoeffs] = useState<number[]>(() => Array(total).fill(1));
  const [justSolved, setJustSolved] = useState(false);

  // Reset coefficients whenever the equation changes.
  useEffect(() => {
    setCoeffs(Array(nR + nP).fill(1));
    setJustSolved(false);
  }, [equation, nR, nP]);

  const react = coeffs.slice(0, nR);
  const prod = coeffs.slice(nR);

  const left = useMemo(() => tally(eq.reactants, react, eq.elements), [eq, react]);
  const right = useMemo(() => tally(eq.products, prod, eq.elements), [eq, prod]);
  const balanced = useMemo(() => isBalanced(eq, react, prod), [eq, react, prod]);

  const fireComplete = () => {
    if (!interacted.current) {
      interacted.current = true;
      onComplete?.({ type: 'equation_balancer', equation });
    }
  };

  // ── Self-demo: nudge the FIRST species partway toward its solved coefficient,
  // then reset to all-1 and hand control to the learner (so they finish). Demo
  // is skipped under reduced motion / when a cue drives, and cancels on any
  // interaction. ───────────────────────────────────────────────────────────
  const [demoSettled, setDemoSettled] = useState(false);
  const demo = useMountSequence(
    solution && solution.length === total
      ? [
          { at: 420, run: () => setCoeffs(applySolvedPrefix(total, solution, 1)) },
          { at: 1020, run: () => setCoeffs(applySolvedPrefix(total, solution, 2)) },
        ]
      : [],
    {
      skip: reduced || hasCue || !solution,
      onSettle: () => {
        setCoeffs(Array(total).fill(1));
        setJustSolved(false);
        setDemoSettled(true);
      },
    },
  );
  // When the demo finishes naturally, settle to the fresh start too.
  useEffect(() => {
    if (demo.done && !reduced && !hasCue && solution) {
      setCoeffs(Array(total).fill(1));
      setDemoSettled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo.done]);

  // ── Cue handling: step / progress drive coefficients toward the solution;
  // highlight spotlights an element in the ledger. ──────────────────────────
  const highlightSet = useMemo(() => new Set(cueHighlights(cue)), [cue]);
  useEffect(() => {
    if (interacted.current) return; // user is adjusting coefficients — don't stomp them
    if (!solution || solution.length !== total) return;
    if (cue?.step != null) {
      setCoeffs(applySolvedPrefix(total, solution, Math.max(0, Math.floor(cue.step))));
      setJustSolved(false);
    } else if (cue?.progress != null) {
      const p = Math.max(0, Math.min(1, cue.progress));
      setCoeffs(solution.map((s) => Math.max(1, Math.round(1 + (s - 1) * p))));
      setJustSolved(p >= 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.step, cue?.progress, solution, total]);

  const cancelDemo = () => {
    if (demo.running) demo.cancel();
  };

  const bump = (i: number, delta: number) => {
    cancelDemo();
    fireComplete();
    setJustSolved(false);
    setCoeffs((c) => {
      const next = [...c];
      next[i] = Math.max(1, Math.min(99, (next[i] ?? 1) + delta));
      return next;
    });
  };

  const solve = () => {
    cancelDemo();
    fireComplete();
    if (solution) {
      setCoeffs(solution);
      setJustSolved(true);
    }
  };

  const reset = () => {
    cancelDemo();
    setCoeffs(Array(total).fill(1));
    setJustSolved(false);
  };

  const balancedCount = eq.elements.filter((el) => left[el] === right[el]).length;

  // WIP-016 — report the current attempt as a step's answer (design §8.3).
  // Fires on every coefficient change (bump / solve / reset) and whenever the
  // equation itself changes; deliberately not gated on `balanced` — a wrong
  // attempt is still an answer, exactly as a wrong free-text one is today.
  useEffect(() => {
    onStateChange?.({
      state: { equation, coeffs },
      display: formatEquationDisplay(eq, coeffs, nR),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equation, coeffs, eq, nR, onStateChange]);

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Reactions · Conservation of mass"
      title={config.title ?? 'Balance the equation'}
      subtitle="Adjust coefficients until every element matches on both sides."
      hint="Use the ± steppers (or ↑↓ while focused) on each species · Solve fills the exact integers."
      actions={
        <div className="flex items-center gap-1.5">
          {showExamples && (
            <select
              value={EXAMPLE_EQUATIONS.some((e) => e.equation === equation) ? equation : ''}
              onChange={(e) => {
                cancelDemo();
                if (e.target.value) setEquation(e.target.value);
              }}
              aria-label="Choose an example equation"
              className={`max-w-[170px] rounded-sm border border-ws-border bg-ws-tile px-2 py-1 text-xs text-ws-ink-2 outline-none transition-colors hover:bg-ws-surface-2 focus:border-ws-accent ${VIZ_FOCUS}`}
            >
              <option value="">Examples…</option>
              {EXAMPLE_EQUATIONS.map((e) => (
                <option key={e.equation} value={e.equation}>
                  {e.label}
                </option>
              ))}
            </select>
          )}
          <button type="button" onClick={reset} className={VIZ_BTN_GHOST} aria-label="Reset coefficients to 1">
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      }
    >
      <div className="relative flex w-full flex-col gap-3">
        <NowYouTry show={demoSettled && !hasCue} />

        {/* The equation with steppers under each species */}
        <div
          className={`flex flex-wrap items-end justify-center gap-x-1.5 gap-y-3 rounded-md border p-4 transition-colors duration-500 ${
            balanced ? 'border-ws-success/60 bg-ws-success/5' : 'border-ws-border bg-ws-surface-2'
          }`}
        >
          {eq.reactants.map((sp, i) => (
            <SpeciesTerm
              key={`r${i}`}
              formula={sp.formula}
              coeff={coeffs[i] ?? 1}
              onBump={(d) => bump(i, d)}
              highlight={justSolved}
              showPlus={i < nR - 1}
            />
          ))}
          <span className="mx-1 select-none self-center pb-6 text-lg font-semibold text-ws-ink-3">→</span>
          {eq.products.map((sp, i) => (
            <SpeciesTerm
              key={`p${i}`}
              formula={sp.formula}
              coeff={coeffs[nR + i] ?? 1}
              onBump={(d) => bump(nR + i, d)}
              highlight={justSolved}
              showPlus={i < nP - 1}
            />
          ))}
        </div>

        {/* Per-element balance ledger */}
        <div className="rounded-md border border-ws-border bg-ws-surface p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">
              Atom balance per element
            </span>
            <span className="text-xs font-medium text-ws-ink-3">
              {balancedCount}/{eq.elements.length} matched
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {eq.elements.map((el) => {
              const ok = left[el] === right[el];
              const spotlight = highlightSet.has(el.toLowerCase());
              return (
                <div
                  key={el}
                  className={`flex items-center gap-1.5 rounded-sm border px-2 py-1 text-xs transition-all duration-300 ${
                    ok
                      ? 'border-ws-success/50 bg-ws-success/10 text-ws-ink'
                      : 'border-ws-border bg-ws-tile text-ws-ink-2'
                  } ${spotlight ? 'ring-2 ring-ws-accent ring-offset-1 ring-offset-ws-surface' : ''}`}
                >
                  <span
                    className="flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold"
                    style={{ backgroundColor: elementColor(el), color: DARK_INK }}
                  >
                    {el}
                  </span>
                  <span className="tabular-nums">
                    {left[el]} <span className="text-ws-ink-4">vs</span> {right[el]}
                  </span>
                  {ok && <Check size={12} className="text-ws-success" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Status + solve */}
        <div className="flex items-center justify-between gap-2">
          <div
            className={`flex items-center gap-1.5 text-sm font-semibold transition-colors ${
              balanced ? 'text-ws-success' : 'text-ws-ink-3'
            }`}
          >
            {balanced ? (
              <>
                <Check size={16} /> Balanced!
              </>
            ) : (
              'Not balanced yet'
            )}
          </div>
          <button type="button" onClick={solve} className={VIZ_BTN_PRIMARY} disabled={!solution}>
            <Sparkles size={13} /> Solve
          </button>
        </div>
      </div>
    </VizCard>
  );
}

// Build a coefficient array where the first `n` species use their solved values
// and the rest stay at 1 — used by the self-demo and cue.step.
function applySolvedPrefix(total: number, solution: number[], n: number): number[] {
  const k = Math.max(0, Math.min(total, n));
  return Array.from({ length: total }, (_, i) => (i < k ? solution[i] : 1));
}

function SpeciesTerm({
  formula,
  coeff,
  onBump,
  highlight,
  showPlus,
}: {
  formula: string;
  coeff: number;
  onBump: (delta: number) => void;
  highlight: boolean;
  showPlus: boolean;
}) {
  return (
    <div className="flex items-end gap-1.5">
      <div className="flex flex-col items-center">
        <div className="mb-1 flex flex-col items-center overflow-hidden rounded-sm border border-ws-border">
          <button
            type="button"
            onClick={() => onBump(1)}
            aria-label={`Increase coefficient for ${formula}`}
            className={`flex h-5 w-7 items-center justify-center bg-ws-tile text-ws-ink-2 transition-colors hover:bg-ws-accent-quiet active:brightness-95 ${VIZ_FOCUS}`}
          >
            <Plus size={12} />
          </button>
          <button
            type="button"
            onClick={() => onBump(-1)}
            aria-label={`Decrease coefficient for ${formula}`}
            className={`flex h-5 w-7 items-center justify-center border-t border-ws-border bg-ws-tile text-ws-ink-2 transition-colors hover:bg-ws-accent-quiet active:brightness-95 ${VIZ_FOCUS}`}
          >
            <Minus size={12} />
          </button>
        </div>
        <div
          className="flex items-baseline gap-0.5"
          role="spinbutton"
          tabIndex={0}
          aria-label={`Coefficient for ${formula}`}
          aria-valuenow={coeff}
          aria-valuemin={1}
          aria-valuemax={99}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
              e.preventDefault();
              onBump(1);
            } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
              e.preventDefault();
              onBump(-1);
            }
          }}
        >
          <span
            className={`min-w-[18px] text-right font-mono text-lg font-bold tabular-nums transition-all duration-300 ${
              coeff > 1 ? 'text-ws-accent' : 'text-ws-ink-4'
            } ${highlight ? 'animate-pulse' : ''}`}
          >
            {coeff}
          </span>
          <span className="font-mono text-lg font-semibold text-ws-ink">{subscriptFormula(formula)}</span>
        </div>
      </div>
      {showPlus && <span className="self-center pb-0.5 text-lg font-semibold text-ws-ink-3">+</span>}
    </div>
  );
}
